"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "@/hooks/use-translations";
import { findScroller } from "./scroller";

/**
 * THE MATCH LADDER. The one mechanic this site owns.
 *
 * Scrolling this section does not advance the page. It holds, and the wall of members
 * narrows: everyone, then your industry, then the people looking for what you offer,
 * then the one you both said yes to.
 *
 * It shows the matching being precise, NOT the network being small. Those are easy to
 * confuse and the difference matters: the product exists to grow somebody's network,
 * so an argument for having fewer people undercuts it. What narrows here is the
 * shortlist, not the ambition.
 *
 * Built on position: sticky and a passive scroll listener. No scroll library, no
 * smooth scroll shim. Only opacity and transform change, so it stays on the
 * compositor and holds frame rate on a mid range phone.
 *
 * IT DOES NOT ASSUME THE WINDOW SCROLLS. The app shell scrolls a container on some
 * platforms, so the listener sits on the document in the capture phase and every
 * measurement is taken against whatever is actually scrolling (see scroller.ts).
 */

type Member = {
  /** Keys into landing.ladder.titles / landing.ladder.trades, so every card translates. */
  title: string;
  trade: string;
  industry: string;
  /* Which stage this member survives to. 0 everyone, 1 your industry, 2 the shortlist,
     3 the one. */
  depth: 0 | 1 | 2 | 3;
};

const TINTS = [
  { bg: "#e0f0ff", fg: "#0a63d1" },
  { bg: "#e0e7ff", fg: "#4338ca" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#fef3c7", fg: "#b45309" },
  { bg: "#fee2e2", fg: "#b91c1c" },
  { bg: "#f3e8ff", fg: "#7e22ce" },
  { bg: "#cffafe", fg: "#0e7490" },
];

/**
 * A believable room. These are member shapes, not testimonials: no invented company
 * names presented as customers, no fake logos, no numbers claiming traction. Roles and
 * industries come from the lists the product actually uses.
 */
const MEMBERS: Member[] = [
  { title: "operationsLead", trade: "manufacturing", industry: "manufacturing", depth: 0 },
  { title: "founder", trade: "hospitality", industry: "hospitality", depth: 0 },
  { title: "managingDirector", trade: "construction", industry: "construction", depth: 0 },
  { title: "founder", trade: "marketingAgency", industry: "marketing", depth: 2 },
  { title: "creativeDirector", trade: "advertising", industry: "marketing", depth: 1 },
  { title: "owner", trade: "retail", industry: "retail", depth: 0 },
  { title: "headOfGrowth", trade: "marketingAgency", industry: "marketing", depth: 1 },
  { title: "founder", trade: "accounting", industry: "finance", depth: 0 },
  { title: "director", trade: "logistics", industry: "logistics", depth: 0 },
  { title: "founder", trade: "brandStudio", industry: "marketing", depth: 3 },
  { title: "partner", trade: "legal", industry: "legal", depth: 0 },
  { title: "owner", trade: "fitness", industry: "health", depth: 0 },
  { title: "founder", trade: "saas", industry: "technology", depth: 0 },
  { title: "headOfPartnerships", trade: "marketingAgency", industry: "marketing", depth: 2 },
  { title: "managingDirector", trade: "recruitment", industry: "recruitment", depth: 0 },
  { title: "owner", trade: "property", industry: "property", depth: 0 },
  { title: "founder", trade: "ecommerce", industry: "retail", depth: 0 },
  { title: "creativeLead", trade: "designStudio", industry: "marketing", depth: 1 },
  { title: "director", trade: "events", industry: "events", depth: 0 },
  { title: "founder", trade: "consultancy", industry: "consulting", depth: 0 },
  { title: "owner", trade: "automotive", industry: "automotive", depth: 0 },
  { title: "headOfSales", trade: "wholesale", industry: "retail", depth: 0 },
  { title: "founder", trade: "photography", industry: "creative", depth: 0 },
  { title: "managingPartner", trade: "finance", industry: "finance", depth: 0 },
  { title: "founder", trade: "interiors", industry: "design", depth: 0 },
  { title: "owner", trade: "catering", industry: "hospitality", depth: 0 },
  { title: "director", trade: "insurance", industry: "finance", depth: 0 },
  { title: "founder", trade: "contentStudio", industry: "marketing", depth: 1 },
  { title: "headOfOps", trade: "manufacturing", industry: "manufacturing", depth: 0 },
  { title: "owner", trade: "salon", industry: "health", depth: 0 },
  { title: "founder", trade: "podcastAgency", industry: "marketing", depth: 1 },
  { title: "managingDirector", trade: "print", industry: "manufacturing", depth: 0 },
  { title: "director", trade: "architecture", industry: "construction", depth: 0 },
  { title: "founder", trade: "coffeeRoastery", industry: "hospitality", depth: 0 },
  { title: "headOfBrand", trade: "marketingAgency", industry: "marketing", depth: 2 },
  { title: "owner", trade: "landscaping", industry: "construction", depth: 0 },
];

const STAGE_KEYS = ["everyone", "industry", "shortlist", "theOne"] as const;

/**
 * The members to put on the wall when only `limit` of them fit.
 *
 * The ladder's whole argument is the counter falling, so a shortened wall has to narrow
 * the way a full one does. Each stage keeps a share of the room, biggest at the bottom:
 * on a six card wall that is six, four, two, one, and on an eighteen card wall it is
 * eighteen, nine, four, one. Keeping "everyone who survives past the first stage" and
 * trimming only the rest looks obvious and is wrong on a small wall, because the
 * survivors alone would fill it and the counter would barely move.
 *
 * The one member who reaches the end is never dropped, and original list order is kept,
 * so a short wall does not read as sorted by how far each member gets.
 */
function pickWall(limit: number | null): Member[] {
  if (limit === null || limit >= MEMBERS.length) return MEMBERS;

  const byDepth = (d: number) => MEMBERS.filter((m) => m.depth === d);
  const quota = [
    limit, // depth 0 takes whatever is left over
    Math.max(1, Math.round(limit * 0.25)),
    Math.max(1, Math.round(limit * 0.2)),
    1, // the one both people said yes to is always on the wall
  ];

  const kept = new Set<Member>();
  // Deepest first, so the stages that carry the argument get their share before the
  // filler does, whatever the limit.
  for (const depth of [3, 2, 1, 0] as const) {
    for (const m of byDepth(depth).slice(0, quota[depth])) {
      if (kept.size >= limit) break;
      kept.add(m);
    }
  }
  return MEMBERS.filter((m) => kept.has(m));
}

export function MatchLadder() {
  const { t } = useTranslations("landing");
  const trackRef = useRef<HTMLDivElement>(null);
  const wallRef = useRef<HTMLUListElement>(null);
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);
  /**
   * The real height of the site header, published as a CSS variable.
   *
   * The sticky stage has to start below the header, and that offset used to be a
   * hardcoded 64px. 64 is what it measures in a desktop browser at a phone viewport, but
   * a real handset adds the safe-area inset, so the header is taller there and the top of
   * the stage, the heading and the top of the count, sits underneath it. That is a fault
   * no viewport size on a Mac reproduces, which is exactly why it kept surviving.
   * Measured, it cannot be wrong on a device nobody here has.
   */
  useEffect(() => {
    const publish = () => {
      const header = document.querySelector("header");
      const h = header ? Math.round(header.getBoundingClientRect().height) : 64;
      document.documentElement.style.setProperty("--gs-header-h", `${h}px`);
    };
    publish();
    window.addEventListener("resize", publish);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(publish) : null;
    const header = document.querySelector("header");
    if (observer && header) observer.observe(header);
    return () => {
      window.removeEventListener("resize", publish);
      observer?.disconnect();
    };
  }, []);

  const [viewHeight, setViewHeight] = useState<number | null>(null);
  const viewHeightRef = useRef<number | null>(null);
  /**
   * How many cards the wall can show without any of them being cut off, MEASURED rather
   * than guessed. Null until the first measurement, which renders the full list.
   *
   * It used to be guessed: everything below 640px got a fixed fifteen cards. Fifteen at
   * three columns is five rows, five rows is 370px, and a phone stage is anywhere from
   * 440px to 800px tall depending on the handset and the browser chrome. On anything
   * under about 390x850 the wall did not fit, and because the stage centres its content
   * and hides its overflow, the part that did not fit was silently thrown away: on a
   * 360x640 handset the heading was sliced off the top and two rows off the bottom, and
   * on iOS the whole wall fell below the fold, which is the "huge gap, just white" this
   * fixes. Measuring the room and showing what fits cannot fail that way on a handset
   * nobody tested.
   */
  const [wallLimit, setWallLimit] = useState<number | null>(null);

  useEffect(() => {
    const el = wallRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const measure = () => {
      /* The column count is whatever the stylesheet decided at this width (three on a
         phone, up to eight on a wide screen), so read it back rather than duplicating
         the breakpoints in here and letting the two drift apart. */
      const cols = getComputedStyle(el).gridTemplateColumns.split(" ").filter(Boolean).length || 3;
      const gap = parseFloat(getComputedStyle(el).rowGap) || 8;
      /* A card is a 22px mark over up to two lines of trade, plus its padding. Below
         this it stops being readable, so it is the floor a row is allowed to be. */
      const minRow = window.innerWidth <= 640 ? 58 : 68;
      const height = el.clientHeight;
      if (height <= 0) return;
      const rows = Math.max(2, Math.floor((height + gap) / (minRow + gap)));
      setWallLimit(Math.min(MEMBERS.length, cols * rows));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const shown = pickWall(wallLimit);

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const scroller = findScroller(el);
    const viewTop = scroller ? scroller.getBoundingClientRect().top : 0;
    /* When the window is the scroller, innerHeight is the LARGE viewport: it counts the
       strip sitting behind the browser's own chrome, which you cannot see. The stage is
       sized from this number, so on iOS, where the landing page has no scroll container
       of its own, the stage was taller than the screen and its bottom half, the wall,
       sat permanently below the fold. visualViewport is what is actually visible. */
    const view = scroller
      ? scroller.clientHeight
      : window.visualViewport?.height ?? window.innerHeight;
    if (view !== viewHeightRef.current) {
      viewHeightRef.current = view;
      setViewHeight(view);
    }
    const total = rect.height - view;
    if (total <= 0) return;
    // 0 at the moment the section locks, 1 as it releases.
    const p = Math.min(1, Math.max(0, (viewTop - rect.top) / total));
    setProgress(p);
    // Four stages, with the last given extra room so the single card is held long
    // enough to be read rather than flashing past.
    setStage(p < 0.22 ? 0 : p < 0.46 ? 1 : p < 0.72 ? 2 : 3);
  }, []);

  useEffect(() => {
    onScroll();
    // Passive, and coalesced to a frame. A listener that runs layout on every scroll
    // event is what makes a sticky section feel heavy.
    let frame = 0;
    const handler = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        onScroll();
      });
    };
    document.addEventListener("scroll", handler, { passive: true, capture: true });
    window.addEventListener("resize", handler, { passive: true });
    /* The visible viewport changes without a window resize when a mobile browser's
       toolbar slides away, and the stage height is measured from it. */
    const vv = window.visualViewport;
    vv?.addEventListener("resize", handler);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(handler);
      observer.observe(document.body);
    }
    return () => {
      document.removeEventListener("scroll", handler, { capture: true });
      window.removeEventListener("resize", handler);
      vv?.removeEventListener("resize", handler);
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [onScroll]);

  const remaining = shown.filter((m) => m.depth >= stage).length;
  /* A phone scrolls in shorter strokes, so the same hold wants slightly less track. */
  const holdMultiplier = viewHeight !== null && viewHeight < 700 ? 3.0 : 3.2;

  return (
    <section
      className="gs-ladder-track"
      ref={trackRef}
      aria-labelledby="matching-heading"
      style={
        {
          height: viewHeight ? `${Math.round(viewHeight * holdMultiplier)}px` : `${holdMultiplier * 100}svh`,
          "--gs-hold-h": viewHeight ? `${viewHeight}px` : undefined,
        } as React.CSSProperties
      }
    >
      {/* The one section that is allowed out of the page container. Everything else
          holds the shared column; a room does not, because a room is full. The wall of
          members runs to both edges and gets cropped by the screen, which is what
          makes it read as a crowd you are standing in rather than a component. */}
      <div className="gs-ladder-stage">
        <div className="gs-ladder-inner gs-ladder-content">
          <h2 id="matching-heading" className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
            {t("ladder.heading")}
          </h2>

          <div className="gs-ladder-body mt-4 grid min-h-0 flex-1 gap-4 md:mt-7 md:gap-10">
            <div className="gs-ladder-copy">
              <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">{t("preview.exampleTag")}</span>
              {/* The counter is the instrument: it is the argument in one number. */}
              <p className="gs-ladder-count text-neutral-900" aria-live="polite">
                {remaining}
              </p>
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">
                {t(`ladder.stages.${STAGE_KEYS[stage]}.label`)}
              </p>
              <p className="mt-3 max-w-[34ch] text-[15px] leading-relaxed text-neutral-600 md:mt-4 md:text-lg">
                {t(`ladder.stages.${STAGE_KEYS[stage]}.note`)}
              </p>

              <div className="mt-5 flex gap-1.5 md:mt-8" aria-hidden="true">
                {STAGE_KEYS.map((key, i) => (
                  <span
                    key={key}
                    className="h-0.5 flex-1 rounded-full transition-colors duration-300"
                    style={{ background: i <= stage ? "var(--color-primary)" : "var(--color-neutral-200, #e5e5e5)" }}
                  />
                ))}
              </div>

              {/* Progress is announced to assistive tech as a number rather than as a
                  bar nobody can perceive. */}
              <p className="sr-only">{t("ladder.srProgress", { stage: stage + 1, total: STAGE_KEYS.length, remaining })}</p>

            </div>

            <ul className="gs-ladder-grid" role="list" ref={wallRef}>
              {shown.map((m, i) => {
                const state = m.depth < stage ? "out" : stage === 3 && m.depth === 3 ? "hero" : "in";
                const tint = TINTS[i % TINTS.length];
                const title = t(`ladder.titles.${m.title}`);
                const trade = t(`ladder.trades.${m.trade}`);
                return (
                  <li
                    key={`${m.title}-${m.trade}-${i}`}
                    className="gs-member"
                    data-state={state}
                    style={{ transitionDelay: `${(i % 6) * 22}ms` }}
                  >
                    <div className="gs-member-top">
                      <span
                        className="gs-member-mark"
                        style={{ background: tint.bg, color: tint.fg }}
                        aria-hidden="true"
                      >
                        {trade.slice(0, 2).toUpperCase()}
                      </span>
                      <span className="min-w-0">
                        <span className="gs-member-name block">{title}</span>
                        <span className="gs-member-role block">{trade}</span>
                      </span>
                    </div>
                    {state !== "out" && <span className="gs-member-tag">{t(`ladder.trades.${m.industry}`)}</span>}
                  </li>
                );
              })}
            </ul>

            {/* Appears only once the room has actually narrowed, so it reads as the
                conclusion of the argument rather than as a banner sitting there. It is a
                row of the section rather than the tail of the copy column: it reserves
                its height either way, and at the bottom that reads as the wall's footer
                instead of as a band of white halfway down the section. */}
            <div
              className="gs-ladder-cta transition-all duration-500"
              style={{
                opacity: stage >= 2 ? 1 : 0,
                transform: stage >= 2 ? "none" : "translateY(10px)",
                pointerEvents: stage >= 2 ? "auto" : "none",
              }}
            >
              <a
                href="/auth/signup"
                className="inline-flex items-center gap-2 rounded-full gradient-primary px-5 py-2.5 text-sm font-bold text-white shadow-[0_12px_30px_-12px_rgba(11,99,224,0.6)] transition-transform hover:-translate-y-0.5 md:px-6 md:py-3"
              >
                {t("ladder.cta")}
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>

        {/* A quiet read-out of the hold, so the section explains itself rather than
            leaving people wondering why the page stopped moving. */}
        <div className="gs-ladder-inner mt-8">
          <div className="h-px w-full bg-neutral-200">
            <div className="h-px transition-none" style={{ width: `${progress * 100}%`, background: "var(--color-primary)" }} />
          </div>
        </div>
      </div>
    </section>
  );
}
